import React, { useEffect, useState } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NumberFormat, { NumberFormatValues, SourceInfo } from 'react-number-format';
import Popup from 'reactjs-popup';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { ItemTagValue } from '../../../core/components/itemTagValue.component';
import { HouseTransferPriceService } from '../../../page/tools/conveyance_deed_cost/houseTransferPrice.service';
import { HouseTransferDataModel } from '../../../page/tools/conveyance_deed_cost/houseTransferPrice.model';
import { ContainerComponent } from '../../../core/components/container.component';
import LayoutDrawer from '../../../core/components/layoutDrawer.component';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CustomFormControl } from '../../../core/components/customFormControl.component';
import { RTLContainer } from '../../../core/components/RTLContainer.component';
import { toPersianNumber } from '../../../core/utils/number.utils';
import { RowComponent } from '../../../core/components/base/row.component';
import { ColumnComponent } from '../../../core/components/base/column.component';
import { NextPage } from 'next';
import { InputLabel, MenuItem, Select, SelectChangeEvent, SwipeableDrawer, TextField } from '@mui/material';
import { useRouter } from 'next/router';

const HouseTransferPrice: NextPage = () => {
  const [calculateResult, setCalculateResult] = useState<HouseTransferDataModel>();
  const [amountText, setAmountText] = useState(0);
  const [spinnerIndex, setSpinnerIndex] = useState('0');
  const [resultOpen, setResultOpen] = React.useState(false);
  const { t } = useTranslation();
  const spinnerHelpArray = [t('financial.housePriceSpinnerHelp1'), <br />, t('financial.housePriceSpinnerHelp2')];
  const amountHelp = t('financial.housePriceValueHelp');
  const houseTransferPriceService = new HouseTransferPriceService();


  const calculatePrice = () => {
    houseTransferPriceService.calculateHouseTransferPrice(Number(spinnerIndex), amountText).subscribe((data) => {
      setCalculateResult(data?.data);
      setResultOpen(true);
    });
  };

  const handleChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    setAmountText(Number(values.value.replace(t('financial.toman'), '').replaceAll(',', '').replaceAll(' ', '')));
  };

  const handleChangeSpinner = (event: SelectChangeEvent) => {
    setSpinnerIndex(event.target.value);
  };

  return (
    <ContainerComponent>
      <RTLContainer>
        <ColumnComponent className="background">
          <ColumnComponent className="mt-5 py-3 totalCenter px-4">
            <RowComponent className="totalCenter">
              <CustomFormControl className="grow" variant="outlined">
                <InputLabel className="mText">{t('financial.sortOfDocument')}</InputLabel>
                <Select
                  defaultValue={'0'}
                  IconComponent={() => <ExpandMoreIcon className="mText" fontSize={'medium'} />}
                  className="w-full"
                  onChange={handleChangeSpinner}
                >
                  <MenuItem key={0} className="text-center text-wrap mText font" value={0}>
                    {t('financial.housePriceType1')}
                  </MenuItem>
                  <MenuItem key={1} className="text-center text-wrap mText font" value={1}>
                    {t('financial.housePriceType2')}
                  </MenuItem>
                </Select>
              </CustomFormControl>
              <Popup
                className="w-auto"
                closeOnDocumentClick
                contentStyle={{ width: '90%', maxHeight: '50vh', overflow: 'scroll', padding: '1rem' }}
                on={['hover', 'focus']}
                trigger={<HelpOutlineIcon className="m-1 w-auto pointer mTextLightBlue" fontSize={'large'} />}
                position={'bottom left'}
              >
                <div className="w-full background overflow-scroll">{spinnerHelpArray}</div>
              </Popup>
            </RowComponent>
            <RowComponent className="grow mt-6 justify-center">
              <CustomFormControl focused className="grow " variant="outlined">
                <InputLabel className="mText">{t('financial.houseAmount')}</InputLabel>
                <NumberFormat
                  customInput={TextField}
                  itemType={'number'}
                  inputMode={'numeric'}
                  placeholder={t('financial.houseAmount')}
                  variant="outlined"
                  thousandSeparator={true}
                  suffix={` ${t('financial.toman')} `}
                  onValueChange={handleChange}
                  autoComplete="off"
                />
              </CustomFormControl>
              <Popup
                on={['hover', 'focus']}
                closeOnDocumentClick
                contentStyle={{ width: '80%', padding: '1rem' }}
                trigger={<HelpOutlineIcon className="m-1 w-auto pointer mTextLightBlue" fontSize={'large'} />}
                position={['bottom right']}
              >
                <div style={{ width: '100%' }}>{amountHelp}</div>
              </Popup>
            </RowComponent>
            <div
              onClick={calculatePrice}
              className={`fixed md:absolute mb-4 calculateButton text-bold text-base text-center container-md bottom-0 md:bottom-20`}
            >
              {t('financial.evaluate')}
            </div>
          </ColumnComponent>
          <SwipeableDrawer
            anchor={'bottom'}
            open={resultOpen}
            PaperProps={{ square: false, elevation: 0, style: { backgroundColor: 'transparent' } }}
            onOpen={() => setResultOpen(true)}
            onClose={() => setResultOpen(!resultOpen)}
          >
            <LayoutDrawer>
              <CloseIcon className="closeIcon" fontSize={'large'} onClick={() => setResultOpen(false)} />
              <div className="text-center w-full text-bold mt-4">{t('financial.houseTransferPrice')}</div>
              <div className="w-full mt-2 separatorLine" />
              <div className="flex flex-col align-end m-3 p-2 rounded-3 cardBackground">
                <ColumnComponent>
                  <ItemTagValue
                    tag={t('financial.writingRight')}
                    value={toPersianNumber(`${calculateResult?.copyright.toLocaleString()} ${t('financial.toman')}`)}
                  />
                  <ItemTagValue
                    tag={t('financial.recordingRight')}
                    value={toPersianNumber(
                      `${calculateResult?.registration_fee.toLocaleString()} ${t('financial.toman')}`
                    )}
                  />
                  <ItemTagValue
                    tag={t('financial.taxOnValueAdded')}
                    value={toPersianNumber(
                      `${calculateResult?.value_added_tax.toLocaleString()} ${t('financial.toman')}`
                    )}
                  />
                  <ItemTagValue
                    tag={t('financial.recordElectricalEvident')}
                    value={toPersianNumber(`${calculateResult?.edr_view.toLocaleString()} ${t('financial.toman')}`)}
                  />
                  <ItemTagValue
                    tag={t('financial.totalSum')}
                    value={toPersianNumber(`${calculateResult?.total.toLocaleString()} ${t('financial.toman')}`)}
                  />
                  <ItemTagValue
                    tag={t('financial.recordingRight2')}
                    value={toPersianNumber(
                      `${calculateResult?.registration_fee_2.toLocaleString()} ${t('financial.toman')}`
                    )}
                  />
                  <ItemTagValue
                    tag={t('financial.totalSumWithRight2')}
                    value={toPersianNumber(`${calculateResult?.total_2.toLocaleString()} ${t('financial.toman')}`)}
                  />
                </ColumnComponent>
              </div>
            </LayoutDrawer>
          </SwipeableDrawer>
        </ColumnComponent>
      </RTLContainer>
    </ContainerComponent>
  );
};

export default HouseTransferPrice;
